import {
  CABINET_PATHS,
  formatWalletAmount,
} from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DashboardRiskAlert,
  DashboardSummary,
  DashboardWarehouseStat,
} from '@nebalkon-workspace/admin-api';
import { AuthService } from '@nebalkon-workspace/auth';
import {
  STAFF_ORDER_HOME_KIND_STATS,
  STAFF_ORDER_HOME_STATUS_FILTERS,
  StaffOrderHomeKindStatItem,
  StaffOrderHomeStatusStatItem,
} from '../orders/staff-order-filters.const';
import { StaffOrderAttentionService } from '../orders/staff-order-attention.service';
import { StaffDashboardService } from './staff-dashboard.service';
import { staffDashboardVisibility } from './staff-dashboard-visibility.util';
import {
  buildDashboardActionAlerts,
  dashboardDebtAgingLabelKey,
  dashboardRiskAlertLabelKey,
} from './staff-dashboard-risks.util';
import {
  formatDashboardVolumeM3,
  formatOccupiedVolumeLabel,
  formatWarehouseVolumeLabel,
  warehouseOccupancyBarWidth,
} from './staff-dashboard-warehouse.util';
import { StaffKpiCardComponent } from './staff-kpi-card.component';

type DashboardNumericSummaryKey =
  | 'clients'
  | 'employees'
  | 'warehouses_active'
  | 'orders_attention'
  | 'boxes_total'
  | 'stored_units'
  | 'boxes_stored'
  | 'boxes_in_transit'
  | 'volume_m3'
  | 'occupancy_percent'
  | 'box_types';

@Component({
  selector: 'lib-staff-home',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, TranslateModule, StaffKpiCardComponent],
  templateUrl: './staff-home.component.html',
  styleUrl: './staff-home.component.scss',
})
export class StaffHomeComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #auth = inject(AuthService);
  readonly #dashboard = inject(StaffDashboardService);
  readonly #orderAttention = inject(StaffOrderAttentionService);

  protected readonly orderKindStats = STAFF_ORDER_HOME_KIND_STATS;
  protected readonly orderHomeStatusStats = STAFF_ORDER_HOME_STATUS_FILTERS;

  protected readonly dashboardData = this.#dashboard.data;
  protected readonly dashboardLoading = this.#dashboard.loading;
  protected readonly dashboardError = this.#dashboard.error;
  protected readonly dashboardAccessDenied = this.#dashboard.accessDenied;

  protected readonly orderStats = this.#orderAttention.stats;
  protected readonly orderStatsLoading = this.#dashboard.loading;
  protected readonly orderStatsAccessDenied = this.#dashboard.accessDenied;
  protected readonly orderStatsError = this.#dashboard.error;
  protected readonly orderStatsAttentionTotal = this.#orderAttention.attentionTotal;

  protected readonly visibility = computed(() =>
    staffDashboardVisibility(this.#auth.roles()),
  );

  protected readonly showAdminLayout = computed(
    () => this.visibility().showAdminLayout,
  );

  protected readonly summary = computed(
    () => this.dashboardData()?.summary ?? null,
  );

  protected readonly risks = computed(() => this.dashboardData()?.risks ?? null);

  protected readonly actionAlerts = computed(() =>
    buildDashboardActionAlerts(this.risks()),
  );

  protected readonly warehouses = computed(
    () => this.dashboardData()?.warehouses ?? [],
  );

  protected readonly ordersAttentionValue = computed(
    () => this.summary()?.orders_attention ?? 0,
  );

  protected readonly volumeLabel = computed(() =>
    formatDashboardVolumeM3(this.summary()?.volume_m3 ?? 0),
  );

  protected readonly occupancyLabel = computed(() => {
    const value = this.summary()?.occupancy_percent ?? 0;
    return `${this.formatDecimal(value)}%`;
  });

  protected readonly clientsBalanceLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.clients_balance_bynd ?? 0),
  );

  protected readonly clientsAvgBalanceLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.clients_avg_balance_bynd ?? 0),
  );

  protected readonly walletActiveAvgBalanceLabel = computed(() =>
    this.formatWalletBynd(
      this.summary()?.wallet_active_clients_avg_balance_bynd ?? 0,
    ),
  );

  protected readonly walletActiveAvgBalanceHintParams = computed(() => ({
    count: this.summary()?.wallet_active_clients_count ?? 0,
  }));

  protected readonly storageAvgBalanceLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.storage_clients_avg_balance_bynd ?? 0),
  );

  protected readonly storageAvgBalanceHintParams = computed(() => ({
    count: this.summary()?.active_storage_clients ?? 0,
  }));

  protected readonly nonStorageBalanceLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.non_storage_clients_balance_bynd ?? 0),
  );

  protected readonly nonStorageBalanceHintParams = computed(() => ({
    count: this.summary()?.non_storage_clients_count ?? 0,
  }));

  protected readonly debtorsTotalLabel = computed(() =>
    this.formatWalletBynd(Math.abs(this.summary()?.debtors_total_bynd ?? 0)),
  );

  protected readonly debtorsShareLabel = computed(() =>
    this.formatPercent(this.summary()?.debtors_share_percent ?? 0),
  );

  protected readonly debtorsShareHintParams = computed(() => ({
    count: this.summary()?.debtors_count ?? 0,
    total: this.summary()?.wallet_clients_count ?? 0,
  }));

  protected readonly storageDailyChargeLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.storage_daily_charge_today_bynd ?? 0),
  );

  protected readonly estimatedTomorrowChargeLabel = computed(() =>
    this.formatWalletBynd(this.summary()?.estimated_daily_charge_tomorrow_bynd ?? 0),
  );

  protected readonly activeStorageClientsValue = computed(
    () => this.summary()?.active_storage_clients ?? 0,
  );

  protected readonly storageChargeTodayHintParams = computed(() => ({
    date: this.formatStorageChargeDate(this.summary()?.storage_charge_date),
  }));

  protected readonly storageChargeDateHintParams = computed(() => ({
    date: this.formatStorageChargeDate(this.summary()?.storage_charge_date),
  }));

  protected readonly debtorsCountValue = computed(
    () => this.summary()?.debtors_count ?? 0,
  );

  protected readonly occupiedVolumeLabel = computed(() =>
    formatOccupiedVolumeLabel(
      this.summary()?.volume_m3 ?? 0,
      this.summary()?.useful_volume_m3 ?? 0,
      this.summary()?.occupancy_percent ?? 0,
    ),
  );

  protected readonly freeVolumeLabel = computed(() =>
    `${formatDashboardVolumeM3(this.summary()?.free_volume_m3 ?? 0)} м³`,
  );

  public ngOnInit(): void {
    this.#dashboard.loadWhenActive();
  }

  protected refreshDashboard(): void {
    this.#dashboard.refresh();
  }

  protected openOrders(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'orders']);
  }

  protected openUsers(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'users']);
  }

  protected openDebtors(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'users'], {
      queryParams: { debtors_only: '1' },
    });
  }

  protected openEmployees(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'employees']);
  }

  protected openWarehouses(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'warehouses']);
  }

  protected openWarehouse(warehouse: DashboardWarehouseStat): void {
    void this.#router.navigate([
      this.#cabinetPaths.staff,
      'warehouses',
      warehouse.id,
    ]);
  }

  protected openBoxTypes(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'box-types']);
  }

  protected openRiskAlert(alert: DashboardRiskAlert): void {
    if (!alert.route) {
      return;
    }

    void this.#router.navigate([this.#cabinetPaths.staff, alert.route], {
      queryParams: alert.query_params,
    });
  }

  protected riskAlertLabel(type: string): string {
    return this.#translate.instant(dashboardRiskAlertLabelKey(type));
  }

  protected debtAgingLabel(label: string): string {
    return this.#translate.instant(dashboardDebtAgingLabelKey(label));
  }

  protected warehouseVolumeLabel(warehouse: DashboardWarehouseStat): string {
    return formatWarehouseVolumeLabel(warehouse);
  }

  protected warehouseBarWidth(warehouse: DashboardWarehouseStat): number {
    return warehouseOccupancyBarWidth(warehouse);
  }

  protected openOrdersByType(kind: StaffOrderHomeKindStatItem): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'orders'], {
      queryParams: { type: kind.id },
    });
  }

  protected openOrdersByTypeAndStatus(
    kind: StaffOrderHomeKindStatItem,
    status: StaffOrderHomeStatusStatItem,
  ): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'orders'], {
      queryParams: { type: kind.id, status: status.id },
    });
  }

  protected orderTypeStatusCount(
    kind: StaffOrderHomeKindStatItem,
    status: StaffOrderHomeStatusStatItem,
  ): number {
    return this.orderStats()?.byTypeAndStatus[kind.id]?.[status.id] ?? 0;
  }

  protected kpiValue(
    summary: DashboardSummary | null,
    key: DashboardNumericSummaryKey,
    fallback = 0,
  ): number {
    if (!summary) {
      return fallback;
    }

    return summary[key] ?? fallback;
  }

  protected storedUnitsValue(summary: DashboardSummary | null): number {
    if (!summary) {
      return 0;
    }

    return summary.stored_units ?? summary.boxes_stored;
  }

  protected formatWalletBynd(value: number): string {
    return `${formatWalletAmount(value)} BYN`;
  }

  protected formatDecimal(value: number): string {
    return value.toFixed(2).replace('.', ',');
  }

  protected formatDebtAgingTotal(value: number): string {
    return this.formatWalletBynd(Math.abs(value));
  }

  private formatPercent(value: number): string {
    return `${value.toFixed(1).replace('.', ',')}%`;
  }

  private formatStorageChargeDate(value: string | undefined): string {
    if (!value) {
      return '';
    }

    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }

    return `${day}.${month}.${year}`;
  }
}
